import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertCircle } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      <Navbar />
      
      <main className="pt-24 pb-20">
        {/* Header */}
        <div className="container mx-auto px-6 mb-12">
          <div className="mb-8">
            <Button variant="ghost" asChild className="gap-2 pl-0 hover:pl-2 transition-all">
              <Link to="/">
                <ArrowLeft className="h-4 w-4" /> Back to Home
              </Link>
            </Button>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Terms of <span className="text-secondary">Service</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            Please read these terms carefully before using WorkRidePro's services. By accessing or using our platform, you agree to be bound by these terms.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Last Updated: November 28, 2024
          </p>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 grid lg:grid-cols-3 gap-12">
          
          {/* Main Text */}
          <div className="lg:col-span-2 space-y-12">
            
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using the WorkRidePro website, mobile application, or services, you agree to comply with and be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">2. Service Description</h2>
              <p className="text-muted-foreground leading-relaxed">
                WorkRidePro provides managed employee transport services, including route planning, fleet management, and tracking via our software platform. We act as an intermediary between corporate clients and transport service providers.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">3. User Responsibilities</h2>
              <p className="text-muted-foreground leading-relaxed">
                As a user of our platform, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Provide accurate and up-to-date information.</li>
                <li>Maintain the confidentiality of your account credentials.</li>
                <li>Use the service only for lawful purposes and in accordance with your organization's policies.</li>
                <li>Not interfere with or disrupt the integrity or performance of the platform.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">4. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                The WorkRidePro platform, including its software, design, logos, and content, is the exclusive property of WorkRidePro and is protected by copyright, trademark, and other intellectual property laws.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">5. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                WorkRidePro shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or in connection with your use of the service.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">6. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these terms at any time. We will notify users of any significant changes. Your continued use of the service after such changes constitutes your acceptance of the new terms.
              </p>
            </section>

          </div>

          {/* Sidebar / Highlights */}
          <div className="lg:col-span-1">
            <div className="bg-slate-50 dark:bg-slate-900 p-8 rounded-2xl border border-border sticky top-24">
              <h3 className="text-xl font-bold text-primary mb-6">Quick Summary</h3>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Agreement</h4>
                    <p className="text-sm text-muted-foreground">Using our app means you agree to these rules.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Scale className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Fair Use</h4>
                    <p className="text-sm text-muted-foreground">Respect the platform and other users.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Liability</h4>
                    <p className="text-sm text-muted-foreground">We provide the platform "as is".</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <p className="text-sm font-medium text-foreground mb-2">Legal Inquiries?</p>
                <a href="mailto:legal@workridepro.com" className="text-secondary font-bold hover:underline">
                  Contact Legal Team
                </a>
              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
